import * as React from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { cn } from '../lib/utils.js';

/**
 * Help Chat Widget — Module #33 AI Help Assistant.
 *
 * A self-contained React component that lets users ask questions and
 * get grounded answers cited from help.auctisync.com articles. Used by:
 *
 *   - apps/help (help.auctisync.com) — anonymous users
 *   - apps/web admin (admin app) — authenticated tenant_staff
 *
 * The caller supplies `getAuthToken` + `baseUrl` so we don't hard-code
 * either. `getAuthToken` may return either a real authenticated JWT
 * (tenant_staff in admin) or a Supabase anon JWT (public help site).
 *
 * Posts to `${baseUrl}/v1/help/ask` and renders the response shape:
 *   { answer, citations: [{article_slug, article_title, section_heading}],
 *     empty, model, retrieved_count }
 */

const HELP_BASE_URL = 'https://help.auctisync.com';

export interface HelpChatCitation {
  article_id: string;
  article_slug: string;
  article_title: string;
  article_summary: string;
  section_heading: string | null;
}

export interface HelpChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  citations?: HelpChatCitation[];
  empty?: boolean;
  error?: string;
}

export interface HelpChatWidgetProps {
  /** Base URL of the IE Worker (e.g., https://auctisync-intelligence.shrill-darkness-8417.workers.dev). */
  baseUrl: string;
  /**
   * Optional callback that returns the JWT to send as Bearer. Omit for anon
   * callers (help.auctisync.com public visitors) — the Worker downgrades to
   * platform-published scope when no Authorization header is present.
   * Authenticated callers (admin app tenant_staff) pass their session JWT.
   */
  getAuthToken?: () => Promise<string>;
  /** Optional welcome message shown before the user types anything. */
  welcomeMessage?: string;
  /** Optional placeholder text inside the input. */
  placeholder?: string;
  /** Optional help-center base URL for citation links. Defaults to help.auctisync.com. */
  helpBaseUrl?: string;
  /** Extra className applied to the outer container. */
  className?: string;
}

/**
 * Compact chat widget. Renders a scrollable message stack with a sticky
 * input bar at the bottom.
 */
export function HelpChatWidget({
  baseUrl,
  getAuthToken,
  welcomeMessage = "Ask anything about using AuctiSync. I'll only answer from the help docs.",
  placeholder = 'Type your question and hit Enter',
  helpBaseUrl = HELP_BASE_URL,
  className,
}: HelpChatWidgetProps): React.ReactElement {
  const [messages, setMessages] = React.useState<HelpChatMessage[]>([]);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  async function send(): Promise<void> {
    const question = draft.trim();
    if (!question || busy) return;
    const userMsg: HelpChatMessage = { id: msgId(), role: 'user', text: question };
    setMessages((m) => [...m, userMsg]);
    setDraft('');
    setBusy(true);
    try {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (getAuthToken) {
        const token = await getAuthToken();
        if (token) headers.authorization = `Bearer ${token}`;
      }
      const res = await fetch(`${baseUrl}/v1/help/ask`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ question }),
      });
      if (!res.ok) {
        let detail = `${res.status}`;
        try {
          const parsed = (await res.json()) as unknown;
          if (typeof parsed === 'object' && parsed !== null) {
            const p = parsed as { error?: unknown; detail?: unknown };
            if (typeof p.detail === 'string') detail = p.detail;
            else if (typeof p.error === 'string') detail = p.error;
          }
        } catch {
          /* ignore parse error */
        }
        setMessages((m) => [
          ...m,
          {
            id: msgId(),
            role: 'assistant',
            text: '',
            error: `Sorry — I couldn't reach the help index. (${detail})`,
          },
        ]);
        return;
      }
      const parsed = (await res.json()) as unknown;
      const body =
        typeof parsed === 'object' && parsed !== null
          ? (parsed as {
              answer?: string;
              citations?: HelpChatCitation[];
              empty?: boolean;
            })
          : { answer: '', citations: [], empty: true };
      const answer = typeof body.answer === 'string' ? body.answer : '';
      const citations = Array.isArray(body.citations) ? body.citations : [];
      const empty = body.empty === true;
      setMessages((m) => [
        ...m,
        {
          id: msgId(),
          role: 'assistant',
          text: answer,
          citations,
          empty,
        },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          id: msgId(),
          role: 'assistant',
          text: '',
          error: err instanceof Error ? err.message : 'Network error',
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <div
      className={cn(
        'flex h-full max-h-[640px] w-full flex-col rounded-lg border bg-[var(--surface)] shadow-sm',
        'border-[var(--border)]',
        className,
      )}
    >
      <header className="border-b border-[var(--border)] px-4 py-3 text-sm font-semibold text-[var(--text)]">
        Ask AuctiSync
      </header>

      <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
        {messages.length === 0 ? (
          <p className="text-[var(--text-muted)]">{welcomeMessage}</p>
        ) : (
          messages.map((m) => <Message key={m.id} m={m} helpBaseUrl={helpBaseUrl} />)
        )}
        {busy ? (
          <div className="text-xs text-[var(--text-muted)]">Looking through the help docs…</div>
        ) : null}
      </div>

      <div className="border-t border-[var(--border)] p-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={2}
          disabled={busy}
          className={cn(
            'block w-full resize-none rounded-md border bg-[var(--surface)] px-3 py-2 text-sm',
            'border-[var(--border)] text-[var(--text)] placeholder:text-[var(--text-muted)]',
            'focus:border-[var(--brand-accent)] focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
        />
        <div className="mt-2 flex items-center justify-between text-xs text-[var(--text-muted)]">
          <span>Enter to send · Shift+Enter for new line</span>
          <button
            type="button"
            onClick={() => void send()}
            disabled={busy || draft.trim().length === 0}
            className={cn(
              'rounded-md px-3 py-1 text-xs font-semibold transition-colors',
              'bg-[var(--brand-accent)] text-[var(--brand-accent-on)]',
              'hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

function Message({
  m,
  helpBaseUrl,
}: {
  m: HelpChatMessage;
  helpBaseUrl: string;
}): React.ReactElement {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-md bg-[var(--surface-muted)] px-3 py-2 text-[var(--text)]">
          {m.text}
        </div>
      </div>
    );
  }
  if (m.error) {
    return (
      <div className="text-[var(--text-error)]">
        <strong>Something went wrong.</strong>
        <span className="ml-1 text-[var(--text-muted)]">{m.error}</span>
      </div>
    );
  }
  // Module #33: render the assistant answer as markdown. It used to render the
  // raw string, so **bold** and [links](url) leaked as literal text. `marked` is
  // the same renderer apps/help uses for article bodies. The parsed HTML runs
  // through DOMPurify before injection — the answer is LLM output on a public
  // surface, so any markup it emits is stripped to a safe subset. User messages
  // above stay plain text — never render user input as HTML.
  const answerHtml = DOMPurify.sanitize(marked.parse(m.text, { async: false }));
  return (
    <div className="space-y-2">
      <div
        className="text-[var(--text)] [&_a]:font-medium [&_a]:text-[var(--brand-accent)] [&_a]:underline [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_strong]:font-semibold [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
        dangerouslySetInnerHTML={{ __html: answerHtml }}
      />
      {m.citations && m.citations.length > 0 ? (
        <div className="border-t border-[var(--border)] pt-2 text-xs text-[var(--text-muted)]">
          <div className="font-semibold tracking-wide uppercase">
            {m.empty ? 'Related articles' : 'Sources'}
          </div>
          <ul className="mt-1 space-y-1">
            {m.citations.map((c) => (
              <li key={c.article_id}>
                <a
                  href={`${helpBaseUrl}/a/${c.article_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-[var(--brand-accent)] hover:underline"
                >
                  {c.article_title}
                </a>
                {c.section_heading ? (
                  <span className="text-[var(--text-muted)]"> · {c.section_heading}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function msgId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
